import { useContext, useMemo } from "react";
import "./MenuSummary.css";
import { Context } from "../../../context/Context";
import { AlertTriangle, Button, Building2, Card, CheckCircle, ClipboardList, EyeOff, FilePenLine, ImagePlus, InfoItem, LayoutGrid, NoticeBox, PackageX, ReceiptText, RefreshCcw, Store, Tags, Utensils } from "../../ui";
import { LIFECYCLE_LABELS } from "../menuRole";
import { additionUsage, menuHealthCounts, summarizeRestaurant } from "../../../utils/menuHealth";
import { summaryText } from "./summaryText";

const LIFECYCLE_BADGE = {
  active: { variant: "success", Icon: CheckCircle },
  draft: { variant: "warning", Icon: FilePenLine },
  archived: { variant: "neutral", Icon: PackageX },
};

// Lifecycle shown as icon + text (never color alone). Used everywhere on the
// Menu page so the three states always look the same.
export function MenuStatusBadge({ lifecycle, size = "md" }) {
  const { lang } = useContext(Context);
  const { variant, Icon } = LIFECYCLE_BADGE[lifecycle] || LIFECYCLE_BADGE.draft;
  return (
    <Tags variant={variant} size={size} icon={<Icon size={size === "sm" ? 12 : 14} />} className="menu-status-badge">
      {LIFECYCLE_LABELS[lang]?.[lifecycle] || lifecycle}
    </Tags>
  );
}

// One compact metric. Either the whole card is a filter shortcut (`onClick`)
// or it only shows information; `rows` are a plain breakdown.
function SummaryCard({ icon, label, value, hint, tone = "neutral", rows, onClick, active, children }) {
  const { lang } = useContext(Context);
  const text = summaryText(lang);
  const shownRows = (rows || []).filter((row) => row.value > 0);
  return (
    <Card padding="sm" hoverable={Boolean(onClick)} onClick={onClick} className={`menu-summary-card tone-${tone} ${onClick ? "is-clickable" : ""} ${active ? "is-active" : ""}`}>
      <div className="menu-summary-card-label">
        <span className="menu-summary-card-icon" aria-hidden="true">
          {icon}
        </span>
        <span>{label}</span>
      </div>
      {value !== undefined && <strong className="menu-summary-card-value">{value}</strong>}
      {children}
      {hint && <span className="menu-summary-card-hint">{hint}</span>}
      {rows && shownRows.length === 0 && tone !== "neutral" && (
        <Tags variant="success" size="sm" icon={<CheckCircle size={12} />}>
          {text.allGood}
        </Tags>
      )}
      {shownRows.length > 0 && (
        <ul className="menu-summary-rows">
          {shownRows.map((row) => (
            <li key={row.key}>
              <span>{row.label}</span>
              <strong>{row.value}</strong>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

const issueTone = (n) => (n > 0 ? "warning" : "success");

function SummarySkeleton({ count = 6 }) {
  return (
    <div className="menu-summary-grid" aria-busy="true">
      {Array.from({ length: count }, (_, i) => (
        <span key={i} className="menu-summary-skeleton" />
      ))}
    </div>
  );
}

// ------------------------------------------------------------------------
// Restaurant level: every menu of the selected restaurant, from the list
// summaries already loaded by the page (no extra requests).
export function MenuOverviewSummary({ menus, status, onRetry, menuFilter, onMenuFilter, itemFilter, onItemFilter, isBranchUser }) {
  const { lang } = useContext(Context);
  const text = summaryText(lang);
  const summary = useMemo(() => summarizeRestaurant(menus), [menus]);
  const { totals, lifecycle, issues } = summary;
  if (status === "ready" && menus.length === 0) return null;

  return (
    <section className="menu-summary menu-summary-overview" aria-labelledby="menu-overview-title">
      <header className="menu-summary-header">
        <h3 id="menu-overview-title">
          <Store size={18} aria-hidden="true" /> {text.overviewTitle}
        </h3>
        <Tags variant="primary" size="sm">
          {text.overviewScope}
        </Tags>
        {status === "ready" && <span className="menu-summary-note">{isBranchUser ? text.branchScopeNote : text.overviewNote}</span>}
      </header>

      {status === "loading" && <SummarySkeleton />}
      {status === "error" && (
        <NoticeBox variant="danger" size="sm" title={text.loadError}>
          <Button type="button" variant="danger" appearance="soft" size="xs" rounded icon={<RefreshCcw size={14} />} onClick={onRetry}>
            {text.retry}
          </Button>
        </NoticeBox>
      )}
      {status === "ready" && (
        <div className="menu-summary-grid">
          <Card padding="sm" hoverable={false} className="menu-summary-card">
            <div className="menu-summary-card-label">
              <span className="menu-summary-card-icon" aria-hidden="true">
                <LayoutGrid size={16} />
              </span>
              <span>{text.menus}</span>
            </div>
            <strong className="menu-summary-card-value">{summary.menus}</strong>
            <div className="menu-summary-status-list">
              {["active", "draft", "archived"].map((key) => (
                <Button
                  key={key}
                  type="button"
                  size="xs"
                  rounded
                  variant={LIFECYCLE_BADGE[key].variant === "neutral" ? "neutral" : LIFECYCLE_BADGE[key].variant}
                  appearance={menuFilter === key ? "filled" : "soft"}
                  aria-pressed={menuFilter === key}
                  icon={(() => {
                    const { Icon } = LIFECYCLE_BADGE[key];
                    return <Icon size={13} />;
                  })()}
                  onClick={() => onMenuFilter(menuFilter === key ? "" : key)}
                >
                  {LIFECYCLE_LABELS[lang]?.[key]} {lifecycle[key]}
                </Button>
              ))}
            </div>
          </Card>

          <SummaryCard
            icon={<AlertTriangle size={16} />}
            label={text.menusWithIssues}
            value={issues.menus}
            hint={text.ofInUse(summary.inUse)}
            tone={issueTone(issues.menus)}
            active={menuFilter === "issues"}
            onClick={issues.menus > 0 ? () => onMenuFilter(menuFilter === "issues" ? "" : "issues") : undefined}
            rows={[
              { key: "itemIssues", label: text.itemIssues, value: issues.itemIssues },
              { key: "noBranches", label: text.noBranches, value: issues.noBranches },
              { key: "noItems", label: text.noItems, value: issues.noItems },
              { key: "emptySections", label: text.emptySectionsLabel, value: issues.emptySections },
            ]}
          />

          <SummaryCard icon={<Utensils size={16} />} label={text.content}>
            <div className="menu-summary-inline">
              <InfoItem label={text.items} value={totals.items + totals.boxes} />
              <InfoItem label={text.sections} value={totals.sections} />
            </div>
            {totals.boxes > 0 && <span className="menu-summary-card-hint">{text.inclBoxes(totals.boxes)}</span>}
          </SummaryCard>

          <SummaryCard
            icon={<EyeOff size={16} />}
            label={text.hiddenItems}
            value={totals.hiddenItems}
            hint={totals.itemsHiddenInSomeBranches > 0 ? text.hiddenInSomeBranches(totals.itemsHiddenInSomeBranches) : undefined}
            active={itemFilter === "hidden"}
            onClick={totals.hiddenItems > 0 ? () => onItemFilter("hidden") : undefined}
          />

          <SummaryCard
            icon={<ImagePlus size={16} />}
            label={text.missingImages}
            value={totals.itemsWithoutImage}
            tone={issueTone(totals.itemsWithoutImage)}
            active={itemFilter === "noImage"}
            onClick={totals.itemsWithoutImage > 0 ? () => onItemFilter("noImage") : undefined}
          />

          <SummaryCard
            icon={<AlertTriangle size={16} />}
            label={text.needsAttention}
            value={totals.itemsNeedingAttention}
            tone={issueTone(totals.itemsNeedingAttention)}
            hint={totals.itemsWithoutDescription > 0 ? text.descriptionsHint(totals.itemsWithoutDescription) : undefined}
            active={itemFilter === "attention"}
            onClick={totals.itemsNeedingAttention > 0 ? () => onItemFilter("attention") : undefined}
            rows={[
              { key: "pricing", label: text.pricingIssues, value: totals.itemsWithPricingIssues },
              { key: "ar", label: text.missingArabicName, value: totals.itemsMissingArabicName },
              { key: "en", label: text.missingEnglishName, value: totals.itemsMissingEnglishName },
            ]}
          />
        </div>
      )}
    </section>
  );
}

// ------------------------------------------------------------------------
// Selected menu: computed from the fully loaded menu, with the same rules the
// item filters use, so "3 pricing issues" filters to exactly 3 items.
export function SelectedMenuSummary({ menu, assignedBranches, itemFilter, onItemFilter, onOpenPanel }) {
  const { lang } = useContext(Context);
  const text = summaryText(lang);
  const counts = useMemo(() => menuHealthCounts(menu), [menu]);
  const additions = useMemo(() => additionUsage(menu), [menu]);
  const totalItems = counts.items + counts.boxes;
  const filterCard = (filter, n) => ({ active: itemFilter === filter, onClick: n > 0 ? () => onItemFilter(itemFilter === filter ? "all" : filter) : undefined });
  const needsBranches = menu.lifecycle === "active" && assignedBranches.length === 0;

  return (
    <section className="menu-summary menu-summary-selected" aria-labelledby="menu-selected-title">
      <header className="menu-summary-header">
        <h3 id="menu-selected-title">
          <span className="menu-summary-scope">{text.selectedTitle}</span>
          <span className="menu-summary-menu-name">{menu.name}</span>
        </h3>
        <MenuStatusBadge lifecycle={menu.lifecycle} />
        {menu.copiedFromMenu && <span className="menu-summary-note">{text.copiedNote}</span>}
        {menu.lifecycle === "draft" && <span className="menu-summary-note">{text.draftNote}</span>}
        {menu.lifecycle === "archived" && <span className="menu-summary-note">{text.archivedNote}</span>}
      </header>

      <div className="menu-summary-grid">
        <SummaryCard icon={<ClipboardList size={16} />} label={text.content} tone={counts.emptySections > 0 ? "warning" : "neutral"}>
          <div className="menu-summary-inline">
            <InfoItem label={text.sections} value={counts.sections} />
            <InfoItem label={text.items} value={totalItems} />
          </div>
          {(counts.boxes > 0 || counts.emptySections > 0) && (
            <span className="menu-summary-card-hint">{[counts.boxes > 0 && text.inclBoxes(counts.boxes), counts.emptySections > 0 && text.emptySections(counts.emptySections)].filter(Boolean).join(" · ")}</span>
          )}
        </SummaryCard>

        <SummaryCard
          icon={<Building2 size={16} />}
          label={text.branches}
          value={assignedBranches.length}
          tone={needsBranches ? "warning" : "neutral"}
          hint={assignedBranches.length ? assignedBranches.map((b) => b.name).join(lang === "ar" ? "، " : ", ") : menu.lifecycle === "active" ? text.notAssigned : undefined}
          onClick={menu.lifecycle !== "archived" ? () => onOpenPanel("branches") : undefined}
        />

        {counts.sections === 0 || totalItems === 0 ? (
          <NoticeBox variant="info" size="sm" className="menu-summary-empty">
            {counts.sections === 0 ? text.noSections : text.noItemsYet}
          </NoticeBox>
        ) : (
          <>
            <SummaryCard icon={<EyeOff size={16} />} label={text.hiddenItems} value={counts.hiddenItems} hint={counts.itemsHiddenInSomeBranches > 0 ? text.hiddenInSomeBranches(counts.itemsHiddenInSomeBranches) : undefined} {...filterCard("hidden", counts.hiddenItems)} />
            <SummaryCard icon={<ImagePlus size={16} />} label={text.missingImages} value={counts.itemsWithoutImage} tone={issueTone(counts.itemsWithoutImage)} {...filterCard("noImage", counts.itemsWithoutImage)} />
            <SummaryCard
              icon={<ReceiptText size={16} />}
              label={text.pricingIssues}
              value={counts.itemsWithPricingIssues}
              tone={issueTone(counts.itemsWithPricingIssues)}
              {...filterCard("pricing", counts.itemsWithPricingIssues)}
              rows={[
                { key: "noPricing", label: text.noPricing, value: counts.itemsWithoutPricing },
                { key: "noBase", label: text.noBasePrice, value: counts.itemsWithoutBasePrice },
                { key: "invalid", label: text.invalidPricing, value: counts.itemsWithInvalidPricing },
              ]}
            />
            <SummaryCard
              icon={<FilePenLine size={16} />}
              label={text.contentQuality}
              value={counts.itemsMissingName}
              tone={issueTone(counts.itemsMissingName)}
              {...filterCard("names", counts.itemsMissingName)}
              rows={[
                { key: "ar", label: text.missingArabicName, value: counts.itemsMissingArabicName },
                { key: "en", label: text.missingEnglishName, value: counts.itemsMissingEnglishName },
              ]}
              hint={counts.itemsWithoutDescription > 0 ? text.descriptionsHint(counts.itemsWithoutDescription) : undefined}
            />
            <SummaryCard
              icon={<ClipboardList size={16} />}
              label={text.additions}
              value={additions.additions}
              tone={additions.broken > 0 ? "warning" : "neutral"}
              hint={[additions.items > 0 && text.additionsOnItems(additions.items), additions.broken > 0 && text.brokenAdditions(additions.broken)].filter(Boolean).join(" · ") || undefined}
              onClick={() => onOpenPanel("additions")}
            />
          </>
        )}
      </div>
    </section>
  );
}
